import { useLoginInfo } from '@/components/LoginInfo/provider';
import { PdfViewer } from '@/components/pdfViewer';
import { useGetEDocPlan } from '@/context/services/e_doc_plan/EDocPlan.service';
import { useGetScanDecision } from '@/features/invoices/components/Details/Components/hooks/useScanDecision';
import { useDownloadClick } from '@/features/invoices/components/Details/Components/Left/hooks';
import { PdfHeader } from '@/features/invoices/components/Details/Components/Left/PdfHeader/index';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { Loading } from '@moneyforward/ap-frontend-components';
import { useGetMfFileAndObjectURL } from '@service/mf_file/MfFile.service';
import { ReceivedInvoiceDetail } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, Suspense, memo, useMemo } from 'react';
import styles from './Left.module.scss';

const cx = classnames.bind(styles);

export type Props = {
  data: ReceivedInvoiceDetail;
};

const InnerLeft: FC<Props> = memo(({ data }) => {
  const { originalBlob, url } = useGetMfFileAndObjectURL(data.mf_file?.id);
  const { apReceiptTypeAlertType } = useGetEDocPlan();
  const { currentOffice } = useLoginInfo();
  const flag = useGetFlags('RECEIVED_INVOICE_DOWNLOAD_XML');

  // Calculate scanDecision based on e_doc_meta_datum properties
  const scanDecision = useGetScanDecision(
    data.mf_file?.e_doc_meta_datum,
    apReceiptTypeAlertType,
    true // Default to true since is_e_doc_ap_receipt_type_enabled is not available
  );

  const showTimestamp = useMemo(
    () =>
      data.mf_file?.e_doc_meta_datum !== null &&
      data.timestamp_status === 'done',
    [data.mf_file?.e_doc_meta_datum, data.timestamp_status]
  );

  const onDownloadClick = useDownloadClick(
    originalBlob,
    data.mf_file?.name || 'unknown.pdf'
  );

  const isPeppolGateway = data.upload_type === 'peppol_gateway' && flag;

  return (
    <div className={cx(styles['container'])}>
      <PdfHeader
        timestamp={showTimestamp}
        scanDecision={scanDecision}
        isEmpty={data.mf_file?.name === undefined}
        receiptType={data.receipt_type ?? undefined}
        isEnabledEDoc={currentOffice?.eDoc.isEnabled}
        onDownloadClick={onDownloadClick}
        isPeppolGateway={isPeppolGateway}
        fileName={data.upload_filename}
        invoiceId={data.id}
      />
      <PdfViewer src={url} />
    </div>
  );
});
InnerLeft.displayName = 'InnerLeft';

const SuspenseLoading: FC = memo(() => {
  return (
    <div>
      <Loading className={cx(styles['spinner'])} size='large' />
    </div>
  );
});
SuspenseLoading.displayName = 'Loading';

export const Left: FC<Props> = memo((props) => {
  return (
    <Suspense fallback={<SuspenseLoading />}>
      <InnerLeft {...props} />
    </Suspense>
  );
});
Left.displayName = 'Left';
